/* eslint-disable react-hooks/exhaustive-deps */

// React/Next imports
import { useMemo, useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';

// Third-party libraries
import { Popover } from '@headlessui/react';
import { usePopper } from 'react-popper';
import clsx from 'clsx';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Utils
import { injectCssForProductFruitChecklistDarkMode } from '@/components/layouts/LayoutPlatform/components/Header/components/OnboardingTasks/utils';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Components
import Icon from '@/components/base/Icon';

const OnboardingTasks = () => {
    const { t, lang } = useTranslation('layout_platform');
    const { productFruits } = window as any;
    const { isOpen, toggle } = useToggle();
    const { isDarkMode } = useDarkMode();
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
    const [popperElement, setPopperElement] = useState<HTMLDivElement>();
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [4, 12]
                }
            }
        ]
    });

    useEffect(() => {
        if (popperElement) {
            initProductFruitCheckList();
        }
        toggle();
    }, [popperElement]);

    const getChecklistId = () => {
        const checklistIds = {
            en: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_EN,
            fr: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_FR,
            es: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_ES
        };

        return (
            checklistIds[lang as keyof typeof checklistIds] ||
            process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_EN
        );
    };

    const initProductFruitCheckList = () => {
        const checklistId = getChecklistId();
        const checkListContainer = document.getElementById('product-fruit-checklist');

        if (productFruits?.api?.checklists && checkListContainer && checklistId) {
            try {
                productFruits.api.checklists.injectToElement(checklistId, checkListContainer);
            } catch (error) {
                console.error('Failed to inject Product Fruits checklist:', error);
            }
        }
        if (isDarkMode()) {
            injectCssForProductFruitChecklistDarkMode();
        }
    };

    return (
        <Popover className="relative">
            <Popover.Button
                ref={setReferenceElement}
                as="button"
                data-testid="onboarding-tasks-button"
                title={t('onboarding.get_started')}
                className={clsx(
                    'relative flex h-12 items-center justify-center gap-2 rounded-full bg-icoGray-100 p-4 dark:bg-icoBlack',
                    {
                        'bg-icoBlue': isOpen,
                        'bg-icoGray-100': !isOpen
                    }
                )}>
                <Icon className="text-icoBlue dark:text-white" icon="checklist" />
                <div className="flex items-center gap-2 font-GSemiBold text-xs">
                    {t('onboarding.get_started')}
                </div>
                <Icon
                    className="text-icoGray-400"
                    icon={isOpen ? 'chevron-top' : 'chevron-bottom'}
                />
            </Popover.Button>
            {useMemo(
                () => (
                    <Popover.Panel
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}>
                        <div
                            data-testid="onboarding-tasks-popover"
                            id="product-fruit-checklist"
                            className="h-500 absolute bottom-0 right-0 w-87.5 rounded-2xl"></div>
                    </Popover.Panel>
                ),
                [isOpen]
            )}
        </Popover>
    );
};

export default OnboardingTasks;
